<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ChatUserTrucks from "@/components/user/UserTrucks.vue";
import UserCard from "@/components/user/UserCard.vue";
import { API } from "@/api/api";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import type { ChatInfoUser } from "@/types/chat";

const props = defineProps<{
  user: ChatInfoUser;
}>();

const { t } = useI18n();
const api = new API();

const trucks = ref<Record<number, TruckDto[]>>({});
const loadingTrucks = ref(false);

function groupTrucksByOwner(items: TruckDto[]): Record<number, TruckDto[]> {
  const grouped: Record<number, TruckDto[]> = {};

  for (const truck of items) {
    const ownerTrucks = grouped[truck.ownerId] ?? [];
    ownerTrucks.push(truck);
    grouped[truck.ownerId] = ownerTrucks;
  }

  return grouped;
}

async function loadCoordinatedTrucks() {
  if (!props.user.isCoordinator) {
    return;
  }

  loadingTrucks.value = true;

  try {
    const resp = await api.trucks.findTrucks({ contactPerson: props.user.id, pageSize: 100 }, false);

    if (resp.success && resp.data) {
      trucks.value = groupTrucksByOwner(resp.data.items);
    }
  } catch (error) {
    console.error(error);
  } finally {
    loadingTrucks.value = false;
  }
}

watch(
  () => props.user,
  () => {
    loadCoordinatedTrucks();
  },
);

onMounted(() => {
  loadCoordinatedTrucks();
});
</script>

<template>
  <div v-if="user.isCoordinator" class="panel-info__block rounded" data-qa="coordinator-info-block">
    <div
      class="panel-info__block-header d-flex justify-space-between bg-blue-lighten-2 pt-2 pb-2 pl-5 pr-5 rounded-b-0 rounded"
    >
      <span class="font-size-12 font-weight-600 text-uppercase text-white">Coordinator</span>
    </div>

    <template v-if="!loadingTrucks">
      <div v-for="(coordinator, index) in user.owners" :key="index" class="panel-info__block-content pa-3">
        <user-card
          :role="t('drivers.owner')"
          :status="coordinator.owner.ownerStatus"
          :user="coordinator.owner"
          role-color="text-green-lighten-1"
        />
        <chat-user-trucks :initial-trucks="trucks[coordinator.ownerId] || []" :user="coordinator.owner" />
        <div v-if="index < user.owners.length - 1" class="short-line" />
      </div>
    </template>
  </div>
</template>

<style scoped>
.panel-info__block {
  margin-bottom: 16px;
  background: #f8f9fa;
  border-bottom: none;
}
</style>
